/**
 * Settings → API Keys tab.
 *
 *   - Customer/vendor admins manage keys for their OWN company (the
 *     company is implicit from their session; no picker).
 *   - Platform admins must pick a target company before listing or
 *     minting (keys are always bound to a customer or vendor).
 *
 * Mint flow: name input → POST /api/admin/api-keys → modal shows the
 * raw key ONCE with a copy button. Closing the modal scrubs the raw
 * value from memory.
 */
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo, fmtUtc } from "../../lib/format";
import {
  ALL_API_SCOPES,
  SCOPE_LABEL,
  type ApiKeyCreateResponse,
  type ApiKeyRow,
  type ApiScope,
  createApiKey,
  listApiKeys,
  revokeApiKey,
  setApiKeyRateLimit,
} from "../../services/apiKeysAdminApi";
import { fetchCompanies, type Company } from "../../services/adminApi";

export const ApiKeysTab: React.FC = () => {
  const { token, role, user } = useAuth();
  const isPlatform = role === "platform";

  // Target company. For non-platform users this is locked to their
  // own; for platform users they pick.
  const [companies, setCompanies] = useState<Company[]>([]);
  const [targetCid, setTargetCid] = useState<number | null>(
    isPlatform ? null : (user?.company_id ?? null),
  );

  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  // Default to all-scopes checked — same behavior as before this UI existed.
  // Operators tightening a key uncheck what they don't need.
  const [newScopes, setNewScopes] = useState<Set<ApiScope>>(
    () => new Set(ALL_API_SCOPES),
  );
  // "" = use the company default (server fills with MttrSettings value);
  // "0" = unlimited; otherwise the numeric per-minute cap.
  const [newRateLimit, setNewRateLimit] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<ApiKeyCreateResponse | null>(null);

  const toggleScope = (s: ApiScope): void => {
    setNewScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  // Platform: fetch the company list for the picker. Filter to customer
  // + vendor (we don't mint keys for 'platform' companies).
  useEffect(() => {
    if (!isPlatform || !token) return;
    fetchCompanies(token)
      .then((cs) =>
        setCompanies(cs.filter((c) => c.company_type === "customer" || c.company_type === "vendor")),
      )
      .catch(() => setCompanies([]));
  }, [isPlatform, token]);

  const load = useCallback(async () => {
    if (!token || targetCid == null) return;
    setLoading(true);
    setError(null);
    try {
      const p = await listApiKeys(token, isPlatform ? targetCid : undefined);
      setKeys(p.keys);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, targetCid, isPlatform]);

  useEffect(() => {
    void load();
  }, [load]);

  const onMint = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token || !newName.trim() || targetCid == null) return;
    if (newScopes.size === 0) {
      setError("Pick at least one scope — a key with no scopes can't call anything.");
      return;
    }
    let rateLimit: number | null | undefined;
    const rlTrim = newRateLimit.trim();
    if (rlTrim === "") {
      rateLimit = undefined; // server uses the default
    } else {
      const n = Number(rlTrim);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
        setError("Rate limit must be a non-negative integer (or blank for default).");
        return;
      }
      rateLimit = n;
    }
    setBusy(true);
    setError(null);
    try {
      const allChecked = newScopes.size === ALL_API_SCOPES.length;
      const resp = await createApiKey(token, {
        name: newName.trim(),
        ...(isPlatform ? { company_id: targetCid } : {}),
        ...(allChecked ? {} : { scopes: Array.from(newScopes) }),
        ...(rateLimit === undefined ? {} : { rate_limit_per_minute: rateLimit }),
      });
      setMinted(resp);
      setNewName("");
      setNewScopes(new Set(ALL_API_SCOPES));
      setNewRateLimit("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSetRateLimit = async (keyId: number, currentValue: number | null): Promise<void> => {
    if (!token) return;
    const cur = currentValue == null ? "" : String(currentValue);
    const raw = window.prompt(
      "Per-minute rate limit. Leave blank for the system default; enter 0 for unlimited.",
      cur,
    );
    if (raw === null) return; // user cancelled
    const trimmed = raw.trim();
    let next: number | null;
    if (trimmed === "") {
      next = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
        setError("Rate limit must be a non-negative integer.");
        return;
      }
      next = n;
    }
    setBusy(true);
    setError(null);
    try {
      await setApiKeyRateLimit(token, keyId, next);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (keyId: number, name: string): Promise<void> => {
    if (!token) return;
    if (!window.confirm(`Revoke API key "${name}"? Any integration using it will start getting 401s immediately.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await revokeApiKey(token, keyId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 max-w-5xl">
      <div className="text-xs text-secondary-500">
        Keys grant programmatic read access to <code>/api/v1/*</code> for
        the bound company. Interactive docs:{" "}
        <a
          href="/api/v1/docs"
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 hover:underline"
        >
          /api/v1/docs ↗
        </a>{" "}
        · machine-readable spec at <code>/api/v1/openapi.json</code>.
      </div>

      {/* Company picker (platform only). */}
      {isPlatform && (
        <div className="flex items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3">
          <label className="text-xs text-secondary-600 flex-1">
            <div>Target company</div>
            <select
              value={targetCid ?? ""}
              onChange={(e) => setTargetCid(e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value="">— Pick a company —</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.company_name} ({c.company_type})
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {targetCid == null ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          Pick a target company above to manage its API keys.
        </div>
      ) : (
        <>
          {/* Mint form */}
          <form
            onSubmit={onMint}
            className="bg-secondary-50 border border-secondary-200 rounded-md p-3 space-y-2"
          >
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-secondary-600 flex-1 min-w-[240px]">
                <div>New key — descriptive name</div>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Production ETL, Mobile app, Postman"
                  maxLength={128}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
              <Button
                type="submit"
                size="sm"
                disabled={busy || !newName.trim() || newScopes.size === 0}
              >
                {busy ? "Minting…" : "Mint key"}
              </Button>
            </div>
            <div>
              <div className="text-xs text-secondary-600 mb-1">
                Scopes — which endpoints this key can call
              </div>
              <div className="flex flex-wrap gap-3">
                {ALL_API_SCOPES.map((s) => (
                  <label
                    key={s}
                    className="text-xs text-secondary-700 flex items-center gap-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={newScopes.has(s)}
                      onChange={() => toggleScope(s)}
                    />
                    <span>{SCOPE_LABEL[s]}</span>
                    <code className="text-[10px] text-secondary-500">({s})</code>
                  </label>
                ))}
              </div>
            </div>
            <label className="text-xs text-secondary-600 flex items-center gap-2">
              <span>Rate limit (per minute)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={newRateLimit}
                onChange={(e) => setNewRateLimit(e.target.value)}
                placeholder="default"
                className="w-24 border border-secondary-300 rounded px-2 py-1 text-sm"
              />
              <span className="text-[11px] text-secondary-500">
                blank = system default · 0 = unlimited
              </span>
            </label>
          </form>

          {/* Key list */}
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-secondary-50 text-secondary-700 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Scopes</th>
                  <th className="text-left px-3 py-2">Rate limit</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Last used</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {loading && keys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-secondary-500">
                      loading…
                    </td>
                  </tr>
                ) : keys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-secondary-500">
                      No keys yet. Mint one above to get started.
                    </td>
                  </tr>
                ) : (
                  keys.map((k) => (
                    <tr key={k.key_id} className="hover:bg-secondary-50 align-top">
                      <td className="px-3 py-2 font-medium">
                        {k.name}
                        <div className="text-[10px] text-secondary-500">
                          by {k.created_by}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {k.scopes.length === 0 ? (
                          <span
                            className="text-[10px] uppercase tracking-wide text-warning-700 bg-warning-50 border border-warning-200 rounded px-1.5 py-0.5"
                            title="No scope restriction — this key can call every /api/v1 endpoint"
                          >
                            all
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {k.scopes.map((s) => (
                              <span
                                key={s}
                                className="text-[10px] bg-secondary-100 border border-secondary-200 text-secondary-700 rounded px-1.5 py-0.5"
                                title={s}
                              >
                                {s.split(":")[0]}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-secondary-600">
                        {k.rate_limit_per_minute === null ? (
                          <span className="text-secondary-500" title="Uses MttrSettings.PublicApiDefaultRateLimit">
                            default
                          </span>
                        ) : k.rate_limit_per_minute === 0 ? (
                          <span className="text-success-700" title="No rate limit enforced">
                            unlimited
                          </span>
                        ) : (
                          <span title="Requests per minute">
                            {k.rate_limit_per_minute}/min
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-secondary-600">
                        {fmtUtc(k.created_utc)}
                      </td>
                      <td className="px-3 py-2 text-secondary-600">
                        {k.last_used_utc ? (
                          <>
                            {fmtAgo(k.last_used_utc)} ago
                            {k.last_used_ip && (
                              <div className="text-[10px] text-secondary-400">
                                from {k.last_used_ip}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-secondary-400">never</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {k.revoked_utc ? (
                          <span
                            className="text-error-700"
                            title={`revoked by ${k.revoked_by ?? "?"} at ${fmtUtc(k.revoked_utc)}`}
                          >
                            ✕ revoked
                          </span>
                        ) : (
                          <span className="text-success-700">✓ active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!k.revoked_utc && (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void onSetRateLimit(k.key_id, k.rate_limit_per_minute)
                              }
                              disabled={busy}
                              title="Adjust requests-per-minute cap"
                            >
                              Edit limit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void onRevoke(k.key_id, k.name)}
                              disabled={busy}
                            >
                              Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Mint-result modal — the only time the raw key is ever surfaced. */}
      {minted && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-5">
            <h2 className="text-base font-semibold text-secondary-900">
              Key minted: {minted.name}
            </h2>
            <div className="text-xs text-secondary-600 mt-1">
              Scopes:{" "}
              {minted.scopes.length === 0 ? (
                <span className="text-warning-700">all (unrestricted)</span>
              ) : (
                minted.scopes.join(", ")
              )}
              {" · "}
              Rate limit:{" "}
              {minted.rate_limit_per_minute === null ? (
                <span className="text-secondary-500">default</span>
              ) : minted.rate_limit_per_minute === 0 ? (
                <span className="text-warning-700">unlimited</span>
              ) : (
                <span>{minted.rate_limit_per_minute}/min</span>
              )}
            </div>
            <p className="text-sm text-warning-800 bg-warning-50 border border-warning-300 rounded p-2 mt-2">
              {minted.warning}
            </p>
            <div className="mt-3">
              <div className="text-xs text-secondary-500 mb-1">Raw key</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-secondary-50 border border-secondary-200 rounded px-2 py-1.5 break-all">
                  {minted.raw_key}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(minted.raw_key);
                  }}
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
              </div>
              <p className="text-[11px] text-secondary-500 mt-2">
                Use in an{" "}
                <code className="px-1 bg-secondary-100 rounded">X-API-Key</code>{" "}
                header on requests to <code>/api/v1/*</code>.
              </p>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setMinted(null)}>I've saved it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysTab;
